import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface TemplateVariable {
  key: string;
  label: string;
  type: 'text' | 'html' | 'image' | 'url';
  required: boolean;
  default_value?: string;
  description?: string;
}

export interface EmailTemplate {
  id: number;
  name: string;
  description: string;
  category: string;
  subject_template: string;
  content_html_template: string;
  content_text_template: string | null;
  variables_schema: TemplateVariable[] | null;
  segment_tags: string[] | null;
  personalization_level: 'light' | 'medium' | 'heavy';
  is_active: boolean;
  usage_count: number;
  last_used_at: Date | null;
  created_by: string | null;
  created_at: Date;
  updated_at: Date | null;
}

export interface CreateTemplateData {
  name: string;
  description?: string;
  category: string;
  subject_template: string;
  content_html_template: string;
  content_text_template?: string;
  variables_schema?: TemplateVariable[];
  segment_tags?: string[];
  personalization_level?: 'light' | 'medium' | 'heavy';
}

export interface TemplateFilters {
  category?: string;
  is_active?: boolean;
  segment_tags?: string[];
  search?: string;
  limit?: number;
  offset?: number;
}

export interface TemplatePreview {
  subject: string;
  html_content: string;
  text_content: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

class TemplateService {
  /**
   * Get all templates with optional filtering
   */
  async getTemplates(filters: TemplateFilters = {}): Promise<{
    templates: EmailTemplate[];
    total: number;
  }> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });

    const response = await fetch(`${API_BASE_URL}/api/templates?${params}`, {
      headers: authService.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch templates');
    }

    return response.json();
  }

  /**
   * Get a single template by ID
   */
  async getTemplate(id: number): Promise<EmailTemplate> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${id}`, {
      headers: authService.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch template');
    }

    return response.json();
  }

  /**
   * Create a new template
   */
  async createTemplate(templateData: CreateTemplateData): Promise<{
    template_id: number;
    template: EmailTemplate;
    validation_warnings: string[];
  }> {
    const response = await fetch(`${API_BASE_URL}/api/templates`, {
      method: 'POST',
      headers: {
        ...authService.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create template');
    }

    return response.json();
  }

  /**
   * Update an existing template
   */
  async updateTemplate(id: number, updates: Partial<CreateTemplateData> & { is_active?: boolean }): Promise<EmailTemplate> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${id}`, {
      method: 'PUT',
      headers: {
        ...authService.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update template');
    }

    const result = await response.json();
    return result.template;
  }

  /**
   * Delete a template
   */
  async deleteTemplate(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${id}`, {
      method: 'DELETE',
      headers: authService.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete template');
    }
  }

  /**
   * Preview template with sample data
   */
  async previewTemplate(id: number, variables?: Record<string, any>): Promise<{
    preview: TemplatePreview;
    template_info: {
      id: number;
      name: string;
      category: string;
    };
    variables_used: Record<string, any>;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${id}/preview`, {
      method: 'POST',
      headers: {
        ...authService.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ variables: variables || {} })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to preview template');
    }

    return response.json();
  }

  /**
   * Get template categories with counts
   */
  async getCategories(): Promise<{ category: string; count: number }[]> {
    const response = await fetch(`${API_BASE_URL}/api/templates/categories`, {
      headers: authService.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch categories');
    }

    return response.json();
  }

  /**
   * Validate template data
   */
  async validateTemplate(templateData: CreateTemplateData): Promise<ValidationResult> {
    const response = await fetch(`${API_BASE_URL}/api/templates/validate`, {
      method: 'POST',
      headers: {
        ...authService.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to validate template');
    }

    const result = await response.json();
    return result.validation;
  }

  /**
   * Extract variables from template content
   */
  async extractVariables(content: {
    subject_template?: string;
    content_html_template?: string;
    content_text_template?: string;
  }): Promise<string[]> {
    const response = await fetch(`${API_BASE_URL}/api/templates/extract-variables`, {
      method: 'POST',
      headers: {
        ...authService.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(content)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to extract variables');
    }

    const result = await response.json();
    return result.variables;
  }

  /**
   * Get template statistics
   */
  async getTemplateStats(): Promise<{
    total_templates: number;
    active_templates: number;
    categories: { category: string; count: number }[];
    most_used: {
      id: number;
      name: string;
      category: string;
      usage_count: number;
      last_used_at: Date | null;
    }[];
    recently_created: {
      id: number;
      name: string;
      category: string;
      created_at: Date;
    }[];
  }> {
    const response = await fetch(`${API_BASE_URL}/api/templates/stats`, {
      headers: authService.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch template statistics');
    }

    return response.json();
  }

  /**
   * Create default templates (admin only)
   */
  async createDefaultTemplates(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/templates/create-defaults`, {
      method: 'POST',
      headers: authService.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create default templates');
    }
  }

  /**
   * Get available templates for campaigns
   */
  async getAvailableTemplatesForCampaigns(category?: string): Promise<{
    id: number;
    name: string;
    description: string;
    category: string;
    usage_count: number;
    variables_schema: TemplateVariable[] | null;
  }[]> {
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    const response = await fetch(`${API_BASE_URL}/api/campaigns/available-templates${params}`, {
      headers: authService.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch available templates');
    }

    const result = await response.json();
    return result.templates;
  }

  /**
   * Test template generation
   */
  async testTemplateGeneration(templateId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/test-template-generation`, {
      method: 'POST',
      headers: {
        ...authService.getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ template_id: templateId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Template generation test failed');
    }
  }
}

export const templateService = new TemplateService();
export default templateService;